import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from '@/contexts/AuthContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { RootRedirect } from '@/components/RootRedirect';
import { Toaster } from '@/components/ui/sonner';
import LoginPage from '@/pages/LoginPage';
import MobileLayout from '@/layouts/MobileLayout';
import AdminLayout from '@/layouts/AdminLayout';
import BodegaLayout from '@/layouts/BodegaLayout';
import LogisticaLayout from '@/layouts/LogisticaLayout';
import InventaristaLayout from '@/layouts/InventaristaLayout';

import LocalDashboard from '@/pages/local/LocalDashboard';
import LocalOrderPage from '@/pages/local/LocalOrderPage';
import LocalHistoryPage from '@/pages/local/LocalHistoryPage';
import LocalProfilePage from '@/pages/local/LocalProfilePage';

import AdminDashboard from '@/pages/admin/AdminDashboard';
import AdminItemsPage from '@/pages/admin/AdminItemsPage';
import AdminConsolidatedPage from '@/pages/admin/AdminConsolidatedPage';
import AdminImportPage from '@/pages/admin/AdminImportPage';
import AdminLocationsPage from '@/pages/admin/AdminLocationsPage';
import AdminUsersPage from '@/pages/admin/AdminUsersPage';
import AdminOrdersProviderPage from '@/pages/admin/AdminOrdersProviderPage';
import AdminSettingsPage from '@/pages/admin/AdminSettingsPage';
import AdminInventoryPage from '@/pages/admin/AdminInventoryPage';
import AdminReceptionsPage from '@/pages/admin/AdminReceptionsPage';
import AdminPricesPage from '@/pages/admin/AdminPricesPage';
import AdminSuppliersPage from '@/pages/admin/AdminSuppliersPage';
import AdminReportsPage from '@/pages/admin/AdminReportsPage';

import BodegaInventoryPage from '@/pages/bodega/BodegaInventoryPage';
import BodegaDashboardPage from '@/pages/bodega/BodegaDashboardPage';
import BodegaReceptionPage from '@/pages/bodega/BodegaReceptionPage';

import LogisticaDispatchPage from '@/pages/logistica/LogisticaDispatchPage';
import LogisticaHistoryPage from '@/pages/logistica/LogisticaHistoryPage';
import LogisticaRoutePage from '@/pages/logistica/LogisticaRoutePage';
import LogisticaRouteHistoryPage from '@/pages/logistica/LogisticaRouteHistoryPage';

import AdminLogisticsPage from '@/pages/admin/AdminLogisticsPage';
import AdminLocalInventoriesPage from '@/pages/admin/AdminLocalInventoriesPage';
import AdminShipmentsPage from '@/pages/admin/AdminShipmentsPage';

import InventaristaPage from '@/pages/inventarista/InventaristaPage';
import AdminRecuentosPage from '@/pages/admin/AdminRecuentosPage';
import AdminJustoPage from '@/pages/admin/AdminJustoPage';
import AdminAjustesPage from '@/pages/admin/AdminAjustesPage';
import RecuentosHomePage from '@/pages/recuentos/RecuentosHomePage';
import RecuentoFormPage from '@/pages/recuentos/RecuentoFormPage';

import './App.css';

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
          
          <Route
            path="/local/*"
            element={
              <ProtectedRoute>
                <MobileLayout>
                  <Routes>
                    <Route index element={<LocalDashboard />} />
                    <Route path="order" element={<LocalOrderPage />} />
                    <Route path="history" element={<LocalHistoryPage />} />
                    <Route path="profile" element={<LocalProfilePage />} />
                    <Route path="recuentos" element={<RecuentosHomePage basePath="/local/recuentos" />} />
                    <Route path="recuentos/:id" element={<RecuentoFormPage basePath="/local/recuentos" />} />
                  </Routes>
                </MobileLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/bodega/*"
            element={
              <ProtectedRoute>
                <BodegaLayout>
                  <Routes>
                    <Route index element={<BodegaDashboardPage />} />
                    <Route path="inventory" element={<BodegaInventoryPage />} />
                    <Route path="recepcion" element={<BodegaReceptionPage />} />
                    <Route path="recuentos" element={<RecuentosHomePage basePath="/bodega/recuentos" />} />
                    <Route path="recuentos/:id" element={<RecuentoFormPage basePath="/bodega/recuentos" />} />
                    <Route path="*" element={<BodegaDashboardPage />} />
                  </Routes>
                </BodegaLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute requireAdmin>
                <AdminLayout>
                  <Routes>
                    <Route index element={<AdminDashboard />} />
                    <Route path="locations" element={<AdminLocationsPage />} />
                    <Route path="users" element={<AdminUsersPage />} />
                    <Route path="items" element={<AdminItemsPage />} />
                    <Route path="orders" element={<AdminOrdersProviderPage />} />
                    <Route path="consolidated" element={<AdminConsolidatedPage />} />
                    <Route path="inventory" element={<AdminInventoryPage />} />
                    <Route path="local-inventories" element={<AdminLocalInventoriesPage />} />
                    <Route path="receptions" element={<AdminReceptionsPage />} />
                    <Route path="prices" element={<AdminPricesPage />} />
                    <Route path="suppliers" element={<AdminSuppliersPage />} />
                    <Route path="logistics" element={<AdminLogisticsPage />} />
                    <Route path="shipments" element={<AdminShipmentsPage />} />
                    <Route path="recuentos" element={<AdminRecuentosPage />} />
                    <Route path="justo" element={<AdminJustoPage />} />
                    <Route path="ajustes" element={<AdminAjustesPage />} />
                    <Route path="recuentos/:id" element={<RecuentoFormPage basePath="/admin/recuentos" />} />
                    <Route path="reports" element={<AdminReportsPage />} />
                    <Route path="import" element={<AdminImportPage />} />
                    <Route path="settings" element={<AdminSettingsPage />} />
                    <Route path="*" element={<AdminDashboard />} />
                  </Routes>
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/logistica/*"
            element={
              <ProtectedRoute allowedRoles={['logistica', 'admin']}>
                <LogisticaLayout>
                  <Routes>
                    <Route index element={<LogisticaRoutePage />} />
                    <Route path="dispatch" element={<LogisticaDispatchPage />} />
                    <Route path="history" element={<LogisticaRouteHistoryPage />} />
                    <Route path="*" element={<LogisticaRoutePage />} />
                  </Routes>
                </LogisticaLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/inventarista/*"
            element={
              <ProtectedRoute allowedRoles={['inventarista', 'admin']}>
                <InventaristaLayout>
                  <Routes>
                    <Route index element={<InventaristaPage />} />
                    <Route path="recuentos" element={<RecuentosHomePage basePath="/inventarista/recuentos" />} />
                    <Route path="recuentos/:id" element={<RecuentoFormPage basePath="/inventarista/recuentos" />} />
                    <Route path="*" element={<InventaristaPage />} />
                  </Routes>
                </InventaristaLayout>
              </ProtectedRoute>
            }
          />
          
          <Route path="/" element={<RootRedirect />} />
        </Routes>
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;